import { CheckCircle2, Loader2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CompanyData } from "../companyTypes";
import type { BuyerEnrichment } from "./useBuyerEnrichment";

/** Progress while scanning, then the review table with per-field checkboxes. */
export function BuyerEnrichReview({ enrich, company }: { enrich: BuyerEnrichment; company: CompanyData }) {
  const {
    phase: enrichPhase, result: enrichResult, selected: selectedFields, setSelected: setSelectedFields,
    setPhase: setEnrichPhase, applying: enrichApplying, apply: applyEnrichment,
  } = enrich;
  const c = company.acquisitionCriteria;
  if (enrichPhase !== "website" && !(enrichPhase === "review" && enrichResult)) return null;
  return (
    <>
  {/* Enrichment Progress */}
  {enrichPhase === "website" && (
    <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
        Scanning website & extracting investment criteria...
      </div>
      <div className="mt-2 h-1.5 bg-blue-100 dark:bg-blue-900 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: "60%" }} />
      </div>
      <p className="text-xs text-muted-foreground mt-1">Crawling multiple pages to find all criteria, portfolio, and investment details.</p>
    </div>
  )}

  {/* Enrichment Review — per-field checkboxes + confidence */}
  {enrichPhase === "review" && enrichResult && (() => {
    const w = enrichResult.website || {};
    const a = enrichResult.apollo || {};
    const cur = enrichResult.current || {};
    const conf = w.confidence || {};

    const confDot = (level: string | undefined) => {
      if (level === "high") return <span className="w-2 h-2 rounded-full bg-green-500 inline-block" title="Explicitly stated on website" />;
      if (level === "medium") return <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" title="Inferred from context" />;
      return <span className="w-2 h-2 rounded-full bg-red-400 inline-block" title="Uncertain" />;
    };

    const rows = [
      { key: "firmType", label: "Firm Type", current: cur.subType, found: w.firmType, conf: conf.firmType },
      { key: "dealSize", label: "Deal Size", current: c?.minDealSize ? `${c.minDealSize}–${c.maxDealSize}` : null, found: w.minDealSize ? `${w.minDealSize}–${w.maxDealSize || ""}` : null, conf: conf.dealSize },
      { key: "targetIndustries", label: "Sectors", current: c?.targetIndustries?.join(", "), found: w.targetIndustries?.join(", "), conf: conf.targetIndustries },
      { key: "geoFocus", label: "Geography", current: c?.geoFocus === "focused" ? c?.primaryGeographies?.join(", ") : c?.geoFocus, found: w.geoFocus === "focused" ? `${w.geoFocus}: ${w.primaryGeographies?.join(", ") || ""}` : w.geoFocus, conf: conf.geoFocus },
      { key: "investmentThemes", label: "Themes", current: c?.investmentThemes?.join(", "), found: w.investmentThemes?.join(", "), conf: null },
      { key: "investmentProfile", label: "Description", current: cur.description?.slice(0, 80), found: w.investmentProfile?.slice(0, 80), conf: conf.investmentProfile },
      { key: "portfolioCompanies", label: `Portfolio (${w.portfolioCompanies?.length || 0})`, current: null, found: w.portfolioCompanies?.map((p: any) => p.name).join(", "), conf: conf.portfolioCompanies },
    ].filter(r => r.found);

    return (
      <div className="p-4 rounded-lg bg-blue-50/30 dark:bg-blue-950/10 border border-blue-200 dark:border-blue-800 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium flex items-center gap-1.5"><Zap className="w-4 h-4 text-blue-500" /> Website Enrichment Results</span>
            {enrichResult.pagesScraped && <span className="text-xs text-muted-foreground ml-2">({enrichResult.pagesScraped} pages scanned)</span>}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 min-h-0 text-xs gold-gradient text-primary-foreground font-semibold btn-gold" onClick={applyEnrichment} disabled={enrichApplying || !Object.values(selectedFields).some(Boolean)}>
              {enrichApplying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              Apply Selected
            </Button>
            <Button variant="ghost" size="sm" className="h-7 min-h-0 text-xs" onClick={() => setEnrichPhase("done")}>Skip</Button>
          </div>
        </div>

        {/* Selectable fields from website */}
        <div className="border rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/50">
              <tr>
                <th className="w-8 p-2"></th>
                <th className="text-left p-2 font-medium">Field</th>
                <th className="text-left p-2 font-medium">Current</th>
                <th className="text-left p-2 font-medium">Found on Website</th>
                <th className="w-6 p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map(r => (
                <tr key={r.key} className={selectedFields[r.key] ? "bg-green-50/50 dark:bg-green-950/10" : ""}>
                  <td className="p-2 text-center">
                    <input type="checkbox" checked={!!selectedFields[r.key]}
                      onChange={(e) => setSelectedFields(p => ({ ...p, [r.key]: e.target.checked }))}
                      className="rounded border-gray-300" />
                  </td>
                  <td className="p-2 font-medium text-muted-foreground whitespace-nowrap">{r.label}</td>
                  <td className="p-2 text-muted-foreground max-w-[160px]"><span className="line-clamp-1">{r.current || <span className="italic opacity-50">—</span>}</span></td>
                  <td className="p-2 max-w-[240px]"><span className="line-clamp-2">{r.found}</span></td>
                  <td className="p-2">{r.conf && confDot(r.conf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Apollo metadata (auto-applied) */}
        {a.apolloId && (
          <div className="text-xs text-muted-foreground space-y-0.5 pt-1">
            <p className="font-medium text-foreground">Apollo data (auto-applied):</p>
            {a.apolloId && <p>✓ Apollo profile link</p>}
            {a.fundingHistory?.length > 0 && <p>✓ Funding: {a.fundingHistory.map((f: any) => `${f.amount} (${f.year})`).join(", ")}</p>}
            {a.totalFunding && <p>✓ Total capital: {a.totalFunding}</p>}
            {a.foundedYear && <p>✓ Founded: {a.foundedYear}</p>}
            {a.linkedinUrl && <p>✓ LinkedIn URL</p>}
            {a.phone && <p>✓ Phone: {a.phone}</p>}
          </div>
        )}
      </div>
    );
  })()}
    </>
  );
}
